import {Three as THREE} from "#material/three/exports"
import type {MaterialNodeSettings} from "#material/sockets/parameters"
import type {MappingSockets} from "#material/nodes/vector/mapping/definition"
import type {MappingVectorType} from "#material/nodes/vector/mapping/types"

export function getMappingMatrix(settings: MaterialNodeSettings<MappingSockets>) {
    const location = settings.location ?? {x: 0, y: 0, z: 0}
    const rotation = settings.rotation ?? {x: 0, y: 0, z: 0}
    const scale = settings.scale ?? {x: 1, y: 1, z: 1}
    const vectorType = settings.vectorType ?? "POINT"

    const matrix = new THREE.Matrix4().identity()
    switch (vectorType) {
        case "POINT":
            // Operations on vector: Scale, then rotate, then translate
            // effective expression: ((I * Translation) * Rotation) * Scale) <- (vector)
            matrix.identity()
            matrix.multiply(new THREE.Matrix4().makeTranslation(location.x, location.y, location.z))
            matrix.multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(rotation.x, rotation.y, rotation.z, "XYZ")))
            matrix.multiply(new THREE.Matrix4().makeScale(scale.x, scale.y, scale.z))
            break
        case "VECTOR":
            // same as POINT, but with no translation
            matrix.identity()
            matrix.multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(rotation.x, rotation.y, rotation.z, "XYZ")))
            matrix.multiply(new THREE.Matrix4().makeScale(scale.x, scale.y, scale.z))
            break
        case "NORMAL":
            // divide by scale, then rotate, then normalize? (see Cycles implementation... not sure why it is supposed to work this way.)
            if (scale.x === 0 || scale.y === 0 || scale.z === 0) throw new Error("Scale cannot be zero for NORMAL mapping type")
            matrix.identity()
            matrix.multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(rotation.x, rotation.y, rotation.z, "XYZ")))
            matrix.multiply(new THREE.Matrix4().makeScale(1 / scale.x, 1 / scale.y, 1 / scale.z))
            break
        case "TEXTURE":
            // inverse of POINT
            if (scale.x === 0 || scale.y === 0 || scale.z === 0) throw new Error("Scale cannot be zero for POINT mapping type")
            matrix.identity()
            matrix.multiply(new THREE.Matrix4().makeScale(1 / scale.x, 1 / scale.y, 1 / scale.z))
            matrix.multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(-rotation.x, -rotation.y, -rotation.z, "ZYX")))
            matrix.multiply(new THREE.Matrix4().makeTranslation(-location.x, -location.y, -location.z))
            break
        default:
            throw new Error(`Unhandled mapping type: ${vectorType}`)
    }

    return matrix
}

export type CyclesMappingType = "point" | "vector" | "normal" | "texture"
export const cyclesMappingType = (mappingType?: MappingVectorType): CyclesMappingType | undefined => {
    if (!mappingType) {
        return undefined
    }
    switch (mappingType) {
        case "POINT":
            return "point"
        case "VECTOR":
            return "vector"
        case "NORMAL":
            return "normal"
        case "TEXTURE":
            return "texture"
        default:
            throw new Error(`Unknown mapping type: ${mappingType}`)
    }
}

export const fromCyclesMappingType = (mappingType?: CyclesMappingType): MappingVectorType | undefined => {
    if (!mappingType) {
        return undefined
    }
    switch (mappingType) {
        case "point":
            return "POINT"
        case "vector":
            return "VECTOR"
        case "normal":
            return "NORMAL"
        case "texture":
            return "TEXTURE"
        default:
            throw new Error(`Unknown cycles mapping type: ${mappingType}`)
    }
}
